import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatBRL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: "", sku: "", category: "Feminino", subcategory: "", short_description: "", description: "",
  price: 0, sale_price: null, images: [], sizes: "PP,P,M,G,GG", colors: "Preto,Branco",
  stock: 0, has_variants: false, featured: false, is_new: false, on_sale: false, material: "", care: "", status: "active",
};

const SIZE_SUGGESTIONS = ["PP", "P", "M", "G", "GG", "XGG", "46", "48", "50", "52", "54"];

// Turn stored URL into a browser-loadable URL (absolute for /api/uploads/*)
function displayUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

// [{color, size, stock}] <-> [{color, sizes: [{size, stock}]}] — a UI agrupa por cor,
// mas o backend guarda uma lista plana de variantes (color+size é a chave da combinação).
function groupVariants(list) {
  const groups = [];
  const byColor = {};
  for (const v of list || []) {
    if (!byColor[v.color]) {
      byColor[v.color] = { color: v.color, sizes: [] };
      groups.push(byColor[v.color]);
    }
    byColor[v.color].sizes.push({ size: v.size, stock: v.stock });
  }
  return groups;
}

function flattenVariantGroups(groups) {
  return groups
    .flatMap((g) =>
      (g.sizes || [])
        .filter((s) => String(s.size || "").trim())
        .map((s) => ({ color: String(g.color || "").trim(), size: String(s.size || "").trim(), stock: parseInt(s.stock, 10) || 0 }))
    )
    .filter((v) => v.color);
}

function VariantBuilder({ groups, setGroups }) {
  const addColorGroup = () => setGroups((gs) => [...gs, { color: "", sizes: [{ size: "", stock: 0 }] }]);
  const removeColorGroup = (gi) => setGroups((gs) => gs.filter((_, i) => i !== gi));
  const updateColorName = (gi, val) => setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, color: val } : g)));
  const addSizeRow = (gi) => setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, sizes: [...g.sizes, { size: "", stock: 0 }] } : g)));
  const removeSizeRow = (gi, si) => setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, sizes: g.sizes.filter((_, j) => j !== si) } : g)));
  const updateSizeRow = (gi, si, field, val) =>
    setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, sizes: g.sizes.map((s, j) => (j === si ? { ...s, [field]: val } : s)) } : g)));
  const addSuggestedSize = (gi, sizeLabel) =>
    setGroups((gs) => gs.map((g, i) => {
      if (i !== gi || g.sizes.some((s) => s.size === sizeLabel)) return g;
      return { ...g, sizes: [...g.sizes, { size: sizeLabel, stock: 0 }] };
    }));

  const total = flattenVariantGroups(groups).reduce((sum, v) => sum + v.stock, 0);

  return (
    <div className="space-y-3 border rounded-lg p-3 bg-neutral-50">
      {groups.map((g, gi) => (
        <div key={gi} className="bg-white border rounded-md p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Cor (ex: Preto)"
              value={g.color}
              onChange={(e) => updateColorName(gi, e.target.value)}
              data-testid={`variant-color-${gi}`}
              className="flex-1"
            />
            <Button type="button" size="sm" variant="outline" onClick={() => removeColorGroup(gi)} aria-label="Remover cor">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {SIZE_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addSuggestedSize(gi, s)}
                className="text-[11px] border rounded px-2 py-0.5 text-neutral-600 hover:border-neutral-900"
              >
                +{s}
              </button>
            ))}
          </div>
          <div className="space-y-1">
            {g.sizes.map((s, si) => (
              <div key={si} className="flex items-center gap-2">
                <Input
                  placeholder="Tamanho"
                  value={s.size}
                  onChange={(e) => updateSizeRow(gi, si, "size", e.target.value)}
                  data-testid={`variant-size-${gi}-${si}`}
                  className="w-24"
                />
                <Input
                  type="number"
                  min="0"
                  placeholder="Estoque"
                  value={s.stock}
                  onChange={(e) => updateSizeRow(gi, si, "stock", e.target.value)}
                  data-testid={`variant-stock-${gi}-${si}`}
                  className="w-24"
                />
                <Button type="button" size="sm" variant="outline" onClick={() => removeSizeRow(gi, si)} aria-label="Remover tamanho">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => addSizeRow(gi)}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar tamanho
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={addColorGroup} className="w-full" data-testid="add-color-btn">
        <Plus className="h-4 w-4 mr-1" /> Adicionar cor
      </Button>
      <p className="text-sm text-neutral-600">
        Estoque total (calculado): <strong data-testid="variant-total-stock">{total}</strong> unidade(s)
      </p>
    </div>
  );
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [variantGroups, setVariantGroups] = useState([]);
  const [imageInput, setImageInput] = useState("");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/products?limit=200").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm({ ...EMPTY, images: [] }); setVariantGroups([]); setImageInput(""); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      images: p.images || [],
      sizes: (p.sizes || []).join(","),
      colors: (p.colors || []).join(","),
      sale_price: p.sale_price ?? "",
      has_variants: !!p.has_variants,
    });
    setVariantGroups(p.has_variants ? groupVariants(p.variants) : []);
    setImageInput("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const hasVariants = !!form.has_variants;
      const variants = hasVariants ? flattenVariantGroups(variantGroups) : [];
      const payload = {
        ...form,
        price: parseFloat(form.price),
        sale_price: form.sale_price === "" || form.sale_price == null ? null : parseFloat(form.sale_price),
        images: form.images.filter(Boolean),
        tags: form.tags || [],
        has_variants: hasVariants,
        variants,
      };
      if (hasVariants) {
        payload.colors = [...new Set(variants.map((v) => v.color))];
        payload.sizes = [...new Set(variants.map((v) => v.size))];
        payload.stock = variants.reduce((sum, v) => sum + v.stock, 0);
      } else {
        payload.stock = parseInt(form.stock, 10) || 0;
        payload.sizes = (typeof form.sizes === "string" ? form.sizes.split(",") : form.sizes).map((s) => s.trim()).filter(Boolean);
        payload.colors = (typeof form.colors === "string" ? form.colors.split(",") : form.colors).map((s) => s.trim()).filter(Boolean);
      }
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produto salvo");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Excluído");
    load();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addImageUrl = () => {
    const url = imageInput.trim();
    if (!url) return;
    set("images", [...(form.images || []), url]);
    setImageInput("");
  };

  const removeImage = (idx) => set("images", form.images.filter((_, i) => i !== idx));

  const onFileChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        
        // set uses setter, but images uses closure — force via functional
        setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
      }
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Produtos</h1>
        <Button data-testid="new-product-btn" onClick={startNew} className="bg-[#C91F1F] hover:bg-[#9F1515]"><Plus className="h-4 w-4 mr-1" />Novo produto</Button>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr><th className="p-3">Produto</th><th className="p-3">Categoria</th><th className="p-3">Preço</th><th className="p-3">Estoque</th><th className="p-3">Ações</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-3 flex items-center gap-2">
                  <img src={displayUrl(p.images?.[0])} className="w-10 h-12 rounded object-cover bg-neutral-100" alt="" />
                  <span className="font-medium">{p.name}</span>
                  {p.is_demo && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">DEMO</span>}
                </td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{formatBRL(p.sale_price ?? p.price)}</td>
                <td className="p-3">{p.stock}</td>
                <td className="p-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} /></div>
              <div>
  <Label>Categoria</Label>
  <select
    value={form.category}
    onChange={(e) => set("category", e.target.value)}
    required
    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
  >
    <option value="Feminino">Feminino</option>
    <option value="Masculino">Masculino</option>
    <option value="Infantil">Infantil</option>
    <option value="Acessórios">Acessórios</option>
  </select>
</div>
              <div><Label>Subcategoria</Label><Input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} /></div>
              <div><Label>Preço (R$)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required /></div>
              <div><Label>Preço promo</Label><Input type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} /></div>
              {!form.has_variants && (
                <div><Label>Estoque</Label><Input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} /></div>
              )}
              <div><Label>Status</Label><Input value={form.status} onChange={(e) => set("status", e.target.value)} /></div>
            </div>

            <div>
              <Label>Imagens</Label>
              <div className="space-y-2">
                {form.images?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative aspect-square rounded-md overflow-hidden border">
                        <img src={displayUrl(img)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/95 rounded-full p-1 shadow" aria-label="Remover">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={imageInput} onChange={(e) => setImageInput(e.target.value)} placeholder="Colar URL da imagem" data-testid="image-url-input" />
                  <Button type="button" variant="outline" onClick={addImageUrl}>Adicionar URL</Button>
                </div>
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    onChange={onFileChosen}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-image-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Enviando..." : "Enviar do computador"}
                  </Button>
                  <p className="text-xs text-neutral-500 mt-1">JPG, PNG, WEBP ou GIF até 5 MB.</p>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={!!form.has_variants}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    set("has_variants", checked);
                    if (checked && variantGroups.length === 0) setVariantGroups([{ color: "", sizes: [{ size: "", stock: 0 }] }]);
                  }}
                  data-testid="has-variants-toggle"
                />
                Este produto tem variação de cor/tamanho?
              </label>
            </div>

            {form.has_variants ? (
              <div>
                <Label>Estoque por cor e tamanho</Label>
                <VariantBuilder groups={variantGroups} setGroups={setVariantGroups} />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Tamanhos (vírgula)</Label><Input value={form.sizes} onChange={(e) => set("sizes", e.target.value)} /></div>
                <div><Label>Cores (vírgula)</Label><Input value={form.colors} onChange={(e) => set("colors", e.target.value)} /></div>
              </div>
            )}
            <div><Label>Descrição curta</Label><Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
            <div><Label>Descrição completa</Label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full border rounded-md p-2" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Material</Label><Input value={form.material} onChange={(e) => set("material", e.target.value)} /></div>
              <div><Label>Cuidados</Label><Input value={form.care} onChange={(e) => set("care", e.target.value)} /></div>
            </div>
            <div className="flex gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />Mais Desejado</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_new} onChange={(e) => set("is_new", e.target.checked)} />Novidade</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.on_sale} onChange={(e) => set("on_sale", e.target.checked)} />Promoção</label>
            </div>
            <Button type="submit" className="w-full bg-[#C91F1F] hover:bg-[#9F1515]" data-testid="save-product-btn">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
